import React, { Component } from 'react';
import { connect } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { Chart } from 'primereact/chart';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import LoadingComponent from '../loadingComponent';
import Service from '../../services';
import { baseUrlAdmin } from '../../store/apiConstants';
import './ActionEffectiveness.scss';

/**
 * Action Effectiveness Dashboard
 * Shows which actions work best for different student profiles
 */
class ActionEffectiveness extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            effectivenessData: [],
            summary: {},
            byProfession: {},
            // filterActionType removed
            filterRiskBand: null,
            chartData: null
        };
        this.service = new Service();
        this.toast = React.createRef();
        
        // this.actionTypes removed
        
        this.riskBands = [
            { label: 'All Risk Bands', value: null },
            { label: 'Low', value: 'LOW' },
            { label: 'Medium', value: 'MEDIUM' },
            { label: 'High', value: 'HIGH' },
            { label: 'Critical', value: 'CRITICAL' }
        ];
    }

    componentDidMount() {
        this.fetchEffectivenessData();
        this.fetchByProfession();
    }

    fetchEffectivenessData = async () => {
        this.setState({ loading: true });
        try {
            const academicYear = localStorage.getItem('userAcademicYear');
            const { filterRiskBand } = this.state;
            
            let url = `${baseUrlAdmin}/fee-decision/learning/action-effectiveness?academicYear=${academicYear}`;
            // actionType filter removed
            if (filterRiskBand) url += `&riskBand=${filterRiskBand}`;
            
            const response = await this.service.get(url, true);
            
            if (response && response.status) {
                const { records, summary } = response.res.data;
                this.setState({ 
                    effectivenessData: records || [],
                    summary: summary || {}
                });
                this.prepareChartData(records || []);
            }
        } catch (error) {
            console.error('Error fetching effectiveness data:', error);
            this.showToast('error', 'Error', 'Failed to fetch effectiveness data');
        } finally {
            this.setState({ loading: false });
        }
    }

    fetchByProfession = async () => {
        try {
            const academicYear = localStorage.getItem('userAcademicYear');
            const url = `${baseUrlAdmin}/fee-decision/learning/action-effectiveness/by-profession?academicYear=${academicYear}`;
            
            const response = await this.service.get(url, true);
            
            if (response && response.status) {
                this.setState({ byProfession: response.res.data || {} });
            }
        } catch (error) {
            console.error('Error fetching by profession:', error);
        }
    }

    prepareChartData = (records) => {
        // Use predefined list to ensure consistency on X-axis
        const definedActionTypes = ['WHATSAPP', 'SMS', 'CALL', 'EMAIL', 'MEETING', 'ESCALATION'];
        
        // Filter records based on what is available/filtered, but we want to show all on axis if no filter selected
        // However, if user filtered by 'CALL', we might only want 'CALL'.
        // But request is "show all actions on x-axis".
        // Let's use the defined list.
        
        const colors = {
            'WHATSAPP': '#25D366',
            'SMS': '#FFA500',
            'CALL': '#4285F4',
            'EMAIL': '#EA4335',
            'MEETING': '#9C27B0',
            'ESCALATION': '#F44336'
        };
        
        const chartData = {
            labels: definedActionTypes,
            datasets: [
                {
                    label: 'Effectiveness Score',
                    data: definedActionTypes.map(type => {
                        // If specific action filter is applied, records contains only that. 
                        // But we want to show 0 for others to maintain x-axis structure?
                        // Or just show data for available.
                        // "show all actions on x-axis" implies structure.
                        const rec = records.find(r => r.actionType === type);
                        return rec?.effectivenessScore || 0;
                    }),
                    backgroundColor: definedActionTypes.map(type => colors[type] || '#607D8B')
                }
            ]
        };
        
        this.setState({ chartData });
    }

    showToast = (severity, summary, detail) => {
        this.toast.current?.show({
            severity: severity,
            summary: summary,
            detail: detail,
            life: 3000
        });
    }

    effectivenessScoreTemplate = (rowData) => {
        const score = rowData.effectivenessScore || 0;
        let severity = 'danger';
        if (score >= 70) severity = 'success';
        else if (score >= 50) severity = 'info';
        else if (score >= 30) severity = 'warning';
        
        return <Tag severity={severity} value={`${score}%`} />;
    }

    successRateTemplate = (rowData) => {
        const totalActions = rowData.totalActions || 0;
        const successfulActions = rowData.successfulActions || 0;
        const rate = totalActions > 0 
            ? Math.round((successfulActions / totalActions) * 100) 
            : 0;
        return `${rate}%`;
    }

    paymentRateTemplate = (rowData) => {
        const totalActions = rowData.totalActions || 0;
        const actionsLeadingToPayment = rowData.actionsLeadingToPayment || 0;
        const rate = totalActions > 0 
            ? Math.round((actionsLeadingToPayment / totalActions) * 100) 
            : 0;
        return `${rate}%`;
    }

    amountTemplate = (rowData) => {
        return `₹${rowData.totalAmountCollected?.toLocaleString() || 0}`;
    }

    riskBodyTemplate = (rowData) => {
        const band = rowData.riskBand?.toUpperCase();
        let severity = null;
        let style = null;

        switch (band) {
            case 'CRITICAL': style = { backgroundColor: '#FFA6BC', color: '#BF0031' }; break;
            case 'HIGH': style = { backgroundColor: '#FFEDD5', color: '#C2410C' }; break; // Explicit Dark Orange
            case 'MEDIUM': style = { backgroundColor: '#FEF9C3', color: '#854D0E' }; break; // Explicit Yellow
            case 'LOW': style = { backgroundColor: '#DCFCE7', color: '#268900' }; break;
            default: style = { backgroundColor: '#E6E8EA', color: '#4B5563' };
        }
        
        return <Tag value={rowData.riskBand || 'UNKNOWN'} severity={severity} style={style} />;
    }

    renderSummaryCards() {
        const { summary } = this.state;
        
        return (
            <div className="summary-cards">
                <Card className="summary-card">
                    <div className="card-content">
                        <div className="card-icon">
                            <i className="pi pi-chart-line"></i>
                        </div>
                        <div className="card-details">
                            <div className="card-value">{summary.totalActions || 0}</div>
                            <div className="card-label">Total Actions Tracked</div>
                        </div>
                    </div>
                </Card>
                
                <Card className="summary-card">
                    <div className="card-content">
                        <div className="card-icon success">
                            <i className="pi pi-check-circle"></i>
                        </div>
                        <div className="card-details">
                            <div className="card-value">{summary.overallSuccessRate || 0}%</div>
                            <div className="card-label">Overall Success Rate</div>
                        </div>
                    </div>
                </Card>
                
                <Card className="summary-card">
                    <div className="card-content">
                        <div className="card-icon info">
                            <i className="pi pi-money-bill"></i>
                        </div>
                        <div className="card-details">
                            <div className="card-value">₹{(summary.totalCollected || 0).toLocaleString()}</div>
                            <div className="card-label">Total Amount Collected</div>
                        </div>
                    </div>
                </Card>
                
                <Card className="summary-card">
                    <div className="card-content">
                        <div className="card-icon warning">
                            <i className="pi pi-calculator"></i>
                        </div>
                        <div className="card-details">
                            <div className="card-value">₹{(summary.averageCollectionPerAction || 0).toLocaleString()}</div>
                            <div className="card-label">Avg Per Action</div>
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    renderProfessionAnalysis() {
        const { byProfession } = this.state;
        
        if (!byProfession || Object.keys(byProfession).length === 0) {
            return <div className="no-data">No profession data available</div>;
        }
        
        return (
            <div className="profession-analysis">
                {Object.keys(byProfession).filter(profession => profession).map(profession => (
                    <Card key={profession} className="profession-card">
                        <h3>{profession === 'UNKNOWN' || !profession ? 'General / Unspecified' : profession}</h3>
                        <table className="profession-table">
                            <thead>
                                <tr>
                                    <th>Action Type</th>
                                    <th>Success Rate</th>
                                    <th>Avg Score</th>
                                    <th>Total Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(byProfession[profession] || []).map((item, idx) => (
                                    <tr key={idx}>
                                        <td>{item.actionType || 'N/A'}</td>
                                        <td>{item.successRate || 0}%</td>
                                        <td>
                                            <Tag 
                                                severity={(item.avgScore || 0) >= 70 ? 'success' : (item.avgScore || 0) >= 50 ? 'info' : 'warning'} 
                                                value={`${item.avgScore || 0}%`} 
                                            />
                                        </td>
                                        <td>{item.totalActions || 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>
                ))}
            </div>
        );
    }

    render() {
        const { loading, effectivenessData, chartData, filterActionType, filterRiskBand } = this.state;
        
        if (loading && effectivenessData.length === 0) {
            return <LoadingComponent />;
        }
        
        return (
            <div className="action-effectiveness-container">
                <Toast ref={this.toast} position="bottom-right" />
                
                <div className="page-header">
                    <h2>Action Effectiveness Dashboard</h2>
                    <p>Learn which actions work best for different student profiles</p>
                </div>
                
                {this.renderSummaryCards()}
                
                <div className="filters-section">
                    <Dropdown
                        value={filterRiskBand}
                        options={this.riskBands}
                        onChange={(e) => this.setState({ filterRiskBand: e.value }, this.fetchEffectivenessData)}
                        placeholder="Filter by Risk Band"
                        className="filter-dropdown"
                    />
                </div>
                
                {chartData && (
                    <Card className="chart-card">
                        <h3>Effectiveness Score by Action Type</h3>
                        <Chart type="bar" data={chartData} options={{ maintainAspectRatio: false }} height="300px" />
                    </Card>
                )}
                
                <Card className="table-card">
                    <h3>Detailed Effectiveness Report</h3>
                    <DataTable
                        value={effectivenessData}
                        loading={loading}
                        emptyMessage="No effectiveness data available"
                        paginator
                        rows={10}
                        rowsPerPageOptions={[10, 25, 50]}
                    >
                        <Column field="actionType" header="Action Type" sortable />
                        <Column field="riskBand" header="Risk Band" body={this.riskBodyTemplate} sortable />
                        <Column header="Effectiveness" body={this.effectivenessScoreTemplate} sortable field="effectivenessScore" />
                        <Column field="totalActions" header="Total Actions" sortable />
                        <Column header="Success Rate" body={this.successRateTemplate} />
                        <Column header="Payment Rate" body={this.paymentRateTemplate} />
                        <Column header="Amount Collected" body={this.amountTemplate} sortable field="totalAmountCollected" />
                        <Column field="averageDaysToPayment" header="Avg Days to Payment" sortable />
                    </DataTable>
                </Card>
                
                <Card className="profession-card-container">
                    <h3>Effectiveness by Staff Role</h3>
                    {this.renderProfessionAnalysis()}
                </Card>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    authenticated: state.authenticated
});

export default connect(mapStateToProps)(ActionEffectiveness);
